import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCart,
  removeFromCart,
  getCartTotal,
  CartItem,
  subscribeToCartUpdates
} from '../../Services/cartService';
import { useCompany } from '../../Contexts/CompanyContext';
import { calculateDiscountedPrice, hasDiscount } from '../../Services/productService';

interface SideCartProps {
  isOpen?: boolean;
  onClose?: () => void;
}

/* ================= IMAGE WITH PLACEHOLDER ================= */
interface ProductImageProps {
  src?: string;
  alt: string;
}

const ProductImageWithPlaceholder: React.FC<ProductImageProps> = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const imageSrc = src || 'asset/images/product-placeholder.png';

  return (
    <>
      {!loaded && (
        <img
          src="asset/images/product-placeholder.png"
          alt="placeholder"
        />
      )}
      <img
        src={imageSrc}
        alt={alt}
        style={{
          display: loaded ? 'block' : 'none'
        }}
        onLoad={() => setLoaded(true)}
      />
    </>
  );
};

const SideCart: React.FC<SideCartProps> = ({ isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const { formatPrice, currencySymbol, loading: companyLoading } = useCompany();

  useEffect(() => {
    // Load cart on mount
    setCartItems(getCart());

    // Subscribe to cart updates
    const unsubscribe = subscribeToCartUpdates((updatedCart) => {
      setCartItems(updatedCart);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const handleViewCart = () => {
    navigate("/cart");
    if (onClose) onClose();
  };

  const handleCheckout = () => {
    navigate("/cart");
    if (onClose) onClose();
  };

  const handleRemoveItem = (cartItemId: string) => {
    if (window.confirm('Remove this item from cart?')) {
      removeFromCart(cartItemId);
    }
  };

  const cartTotal = getCartTotal();
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div>
      <div className={`show-header-cart js-panel-cart ${isOpen ? 'show-sidebar' : ''}`}>
        <div className="s-full js-hide-cart" onClick={onClose}></div>

        <div className="header-cart flex-col-l p-l-40 p-r-40">
          <div className="header-cart-title flex-w flex-sb-m p-b-8">
            <span className="mtext-103 cl2">
              Your Cart {cartItems.length > 0 && `(${cartCount})`}
            </span>

            <div 
              className="fs-35 lh-10 cl2 p-lr-5 pointer hov-cl1 trans-04 js-hide-cart"
              onClick={onClose}
              style={{ cursor: 'pointer' }}
            >
              <i className="zmdi zmdi-close"></i>
            </div>
          </div>

          <div className="header-cart-content flex-w js-pscroll">
            {cartItems.length === 0 ? (
              <div className="w-full text-center p-tb-40">
                <p className="stext-115 cl6">Your cart is empty</p>
              </div>
            ) : (
              <>
                <ul className="header-cart-wrapitem w-full">
                  {cartItems.map((item) => {
                    const itemDiscount = item.discount;
                    const discountedPrice = calculateDiscountedPrice(item.price, itemDiscount);
                    const showItemDiscount = hasDiscount(itemDiscount);

                    return (
                      <li key={item.id} className="header-cart-item flex-w flex-t m-b-12">
                        <div 
                          className="header-cart-item-img" 
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          <ProductImageWithPlaceholder
                            src={item.image}
                            alt={item.productName}
                          />
                        </div>

                        <div className="header-cart-item-txt p-t-8">
                          <a 
                            href="#" 
                            className="header-cart-item-name m-b-18 hov-cl1 trans-04"
                            onClick={(e) => e.preventDefault()}
                          >
                            {item.productName}
                          </a>

                          <span className="header-cart-item-info">
                            {item.quantity} x{' '}
                            {showItemDiscount ? (
                              <>
                                <span className="sidecart-price-discounted">
                                  {formatPrice(discountedPrice)}
                                </span>
                                <span className="sidecart-price-original">
                                  {formatPrice(item.price)}
                                </span>
                              </>
                            ) : (
                              formatPrice(item.price)
                            )}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="w-full">
                  <div className="header-cart-total w-full p-tb-40">
                    Total: {formatPrice(cartTotal)}
                  </div>

                  <div className="header-cart-buttons flex-w w-full">
                    {/* <button
                      className="flex-c-m stext-101 cl0 size-107 bg3 bor2 hov-btn3 p-lr-15 trans-04 m-r-8 m-b-10"
                      onClick={handleViewCart}
                    >
                      View Cart
                    </button> */}

                    <button
                      className="w-100 flex-c-m stext-101 cl0 size-107 bg3 bor2 hov-btn3 p-lr-15 trans-04 m-b-10"
                      onClick={handleCheckout}
                    >
                      Check Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .sidecart-price-discounted {
          font-weight: 600;
          color: inherit;
        }
        .sidecart-price-original {
          text-decoration: line-through;
          color: #aaa;
          font-size: 11px;
          margin-left: 4px;
          font-weight: 400;
        }
      `}</style>
    </div>
  );
};

export default SideCart;